import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import { Modal } from "./Modal";
import { HeaderModal } from "./Header";
import { Body } from "./Body";
import { FooterModal } from "./Footer";

import closeIcon from "assets/images/close-icon.svg";
import allImgPaths from "assets/images";
import useAppState from "context/useAppState";
import { cloneDeep } from "lodash";
import { createPatentPresignedUrl, uploadFileToS3, getPatentFigureImages } from "services/project";
import { changeVijilizeImage } from "services/project";
import { toast } from "react-toastify";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlineOutlined";
import { CHANGE_IMAGE_WALKTHROUGH_STEPS } from "utils/walkthroughs";
import { isPublicWalkthroughProject } from "utils/walkthroughLauncher";

const DEMO_LIMITED_MSG = "This is a demo project. Some functionality is limited.";

interface ChangeVijilizeImageModalProps {
  isShow: boolean;
  setIsShow: Dispatch<SetStateAction<boolean>>;
  vijilizeId?: string;
  onImageChanged?: (newImageUrl: string, previewUrl?: string) => void;
  targetPatentId?: string;
  projectId?: string;
  folderType?: "original_images" | "figures";
  imageS3Path?: string; // explicit S3 path to derive IDs from (overrides context originalImagePath)
  successMessage?: string;
}

const ChangeVijilizeImageModal = ({ isShow, setIsShow, vijilizeId, onImageChanged, targetPatentId, projectId, folderType, imageS3Path, successMessage }: ChangeVijilizeImageModalProps) => {
  // Read-only demo project: the modal opens, but the Change action is disabled.
  const isReadOnlyDemo = isPublicWalkthroughProject(projectId);
  const { markdownS3Path, annotationData, patentImage, originalImagePath } = useAppState("vijilize");
  const [manageImg, setManageImg] = useState('sampleImg');
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [imageSizeError, setImageSizeError] = useState(false);
  const [sampleImages, setSampleImages] = useState<any[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  // Load sample images when modal opens
  useEffect(() => {
    if (isShow) {
      fetchSampleImages();
    }
  }, [isShow, originalImagePath]);

  const isMongoId = (val?: string) => !!val && /^[a-f\d]{24}$/i.test(val);

  const trimToFolderPath = (path: string) => {
    // Strip query params first
    const clean = path.split("?")[0];
    // If it ends with a '/' it's already a folder path
    if (clean.endsWith("/")) return clean;
    // Otherwise strip the filename (last segment after the final '/')
    return clean.substring(0, clean.lastIndexOf("/") + 1);
  };

  const fetchSampleImages = async () => {
    const rawPath = imageS3Path || originalImagePath;
    const isValidPath = rawPath &&
      (rawPath.startsWith("s3://") || rawPath.startsWith("https://"));
    const effectivePath = isValidPath ? trimToFolderPath(rawPath!) : rawPath;

    // Read source + target patent IDs from sessionStorage as fallback
    let sourcePatentId: string | undefined;
    let targetPatentIdFromSession: string | undefined;
    try {
      const claimDataStr = sessionStorage.getItem("vijilizeClaimData");
      if (claimDataStr) {
        const claimData = JSON.parse(claimDataStr);
        sourcePatentId = claimData.sourcePatentId || claimData.activeIndex?.patentId;
        targetPatentIdFromSession = claimData.targetDocumentId;
      }
    } catch (e) {}

    const validPatentId = isMongoId(targetPatentId) ? targetPatentId
      : isMongoId(sourcePatentId) ? sourcePatentId : undefined;
    const validTargetPatentId = isMongoId(targetPatentIdFromSession) ? targetPatentIdFromSession : undefined;
    const validProjectId = projectId;

    if (!isValidPath && (!validPatentId || !validProjectId)) {
      toast.error("Image path information not found");
      return;
    }

    setIsLoadingImages(true);
    try {
      const response = isValidPath
        ? await getPatentFigureImages(effectivePath, undefined, undefined, validTargetPatentId, folderType)
        : await getPatentFigureImages(undefined, validPatentId, validProjectId, validTargetPatentId, folderType);
      const images = response?.data || [];
      // On the read-only demo project, only surface the first 10 sample images.
      // setSampleImages(isReadOnlyDemo ? images.slice(0, 10) : images);
      setSampleImages(images);
    } catch (error: any) {
      console.error("Error fetching sample images:", error);
      toast.error("Failed to load sample images");
    } finally {
      setIsLoadingImages(false);
    }
  };

  const handleImages = (imgType: string) => {
    setManageImg(imgType);
  };

  const startWalkthrough = () => {
    driver({
      showProgress: true,
      steps: CHANGE_IMAGE_WALKTHROUGH_STEPS,
    }).drive();
  };

  const onCloseModal = () => {
    setIsShow(false);
    setSelectedFile(null);
    setImageSizeError(false);
    setSampleImages([]);
  };

  const handleImageSelection = (index: number) => {
    const clonedData = cloneDeep(sampleImages);
    clonedData.forEach((item: any, idx: number) => {
      item.selected = idx === index;
    });
    setSampleImages(clonedData);
  };

  const changeImage = async () => {
    if (manageImg === 'customImg') {
      if (!selectedFile) {
        toast.error("Please select an image to upload");
        return;
      }
      setIsUploading(true);
      await uploadCustomImageHandler();
    } else if (manageImg === 'sampleImg') {
      const selectedImage = sampleImages.find((img: any) => img.selected);
      if (!selectedImage) {
        toast.error("Please select a sample image");
        return;
      }
      setIsUploading(true);
      await updateWithSampleImage(selectedImage);
    }
  };

  const updateWithSampleImage = async (selectedImage: any) => {
    try {
      // For sample images, don't call the API - just use the image directly
      // The sample image is already in S3, so we can use its S3 path directly
      const sampleImageKey = selectedImage.key || selectedImage.s3Path;
      
      if (!sampleImageKey) {
        toast.error("Sample image path not found");
        setIsUploading(false);
        return;
      }

      // Extract bucket name from originalImagePath or imageS3Path
      // Format: s3://bucket-name/path or https://bucket.s3.region.amazonaws.com/path
      let bucketName = "demo-ipvijil-patents"; // Default bucket
      const pathForBucket = (originalImagePath?.startsWith("s3://") || originalImagePath?.startsWith("https://"))
        ? originalImagePath
        : imageS3Path;
      if (pathForBucket) {
        if (pathForBucket.startsWith("s3://")) {
          const s3Parts = pathForBucket.substring(5).split("/");
          bucketName = s3Parts[0];
        } else if (pathForBucket.startsWith("https://")) {
          try {
            const url = new URL(pathForBucket);
            const hostnameParts = url.hostname.split(".");
            if (hostnameParts.length > 0) {
              bucketName = hostnameParts[0];
            }
          } catch (e) {
            console.warn("Could not extract bucket from URL, using default");
          }
        }
      }

      // Construct the full S3 path from the key
      const fullS3Path = `s3://${bucketName}/${sampleImageKey}`;

      console.log("Using sample image directly without reuploading:", fullS3Path);

      setIsUploading(false);
      setSelectedFile(null);
      if (successMessage) {
        toast.success(successMessage);
      }
      
      if (onImageChanged) {
        // Pass the S3 path for reanalysis and the presigned URL for preview
        onImageChanged(fullS3Path, selectedImage.url);
      }
      
      onCloseModal();
    } catch (error) {
      console.error("Error selecting sample image:", error);
      toast.error("Failed to select image. Please try again.");
      setIsUploading(false);
    }
  };

  const uploadCustomImageHandler = async () => {
    try {
      const selectedTeam: any = JSON.parse(
        localStorage.getItem("selectedTeam") as string
      );
      const teamId: string = selectedTeam?.currentTeamId;

      if (!vijilizeId) {
        toast.error("Vijilize ID is missing. Please try again.");
        setIsUploading(false);
        return;
      }

      // Get annotation ID from sessionStorage (optional — only present when an image already exists)
      let annotationId = annotationData?.annotationId;
      if (!annotationId) {
        try {
          const claimDataStr = sessionStorage.getItem("vijilizeClaimData");
          if (claimDataStr) {
            annotationId = JSON.parse(claimDataStr).annotationId;
          }
        } catch (e) {}
      }

      // Create a timestamped filename to avoid conflicts
      const newFileName = `${Date.now()}_${selectedFile.name}`;

      // Convert file to base64 for upload
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target?.result as string;

          // Build payload — annotation_id is optional (absent when no existing image)
          const payload: any = {
            file_name: newFileName,
            content_type: selectedFile.type,
            team_id: teamId,
            comparison_id: vijilizeId,
            upload_type: "customImg",
            image_data: base64Data,
          };

          if (annotationId) payload.annotation_id = annotationId;
          if (markdownS3Path) payload.markdown_s3_path = markdownS3Path;

          // Always send target_patent_id + project_id so backend can construct the path
          if (targetPatentId) payload.target_patent_id = targetPatentId;
          if (projectId) payload.project_id = projectId;
          
          await updateVijilizeImageAPI(payload);
        } catch (error) {
          console.error("Error processing image:", error);
          toast.error("Failed to process image. Please try again.");
          setIsUploading(false);
        }
      };
      
      reader.onerror = () => {
        toast.error("Failed to read image file. Please try again.");
        setIsUploading(false);
      };
      
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("File upload failed. Please try again.");
      setIsUploading(false);
    }
  };

  // Get The Pre-Signed URL
  const getPresignedURL = async (file: any) => {
    try {
      const { data } = await createPatentPresignedUrl(file);
      return data;
    } catch (error) {
      console.error("Error getting presigned URL:", error);
      toast.error("File upload failed. Please try again.");
      return null;
    }
  };

  // Upload To S3 With The Pre-Signed URL
  const initiateS3UploadWithPresignedURL = async (presigedURLObj: any) => {
    try {
      const response = await uploadFileToS3({
        url: presigedURLObj.url,
        file: selectedFile,
        type: selectedFile.type,
      });

      return response;
    } catch (error) {
      console.error("Error uploading to S3:", error);
      toast.error("File upload failed. Please try again.");
      return null;
    }
  };

  // Update The Vijilize Image In DB
  const updateVijilizeImageAPI = async (payload: any) => {
    try {
      const response = await changeVijilizeImage(payload);
      console.log("Change vijilize image response:", response);

      setIsUploading(false);
      setSelectedFile(null);
      toast.success("Image updated successfully");
      
      if (onImageChanged && response?.data) {
        // Backend now returns { s3Path, presignedUrl }
        // Pass s3Path for reanalysis and presignedUrl for preview
        if (response.data.s3Path && response.data.presignedUrl) {
          onImageChanged(response.data.s3Path, response.data.presignedUrl);
        } else {
          // Fallback for old response format (just in case)
          onImageChanged(response.data);
        }
      }
      
      onCloseModal();
      
      // Don't refresh the page anymore - let the parent component handle updates
      // setTimeout(() => {
      //   window.location.reload();
      // }, 500);
    } catch (error) {
      console.error("Error updating image:", error);
      toast.error("Failed to update image. Please try again.");
      setIsUploading(false);
    }
  };

  const handleChange = (file: any) => {
    const maxSizeInMB = 5;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    
    if (file) {
      setSelectedFile(null);
      
      if (file.size > maxSizeInBytes) {
        setImageSizeError(true);
        return;
      }

      setImageSizeError(false);
      setSelectedFile(file);
    }
  };

  const getFileSize = (sizeInBytes: any) => {
    if (sizeInBytes) {
      const sizeInKB = sizeInBytes / 1024;
      if (sizeInKB < 1024) {
        return sizeInKB.toFixed(2) + " KB";
      } else {
        const sizeInMB = sizeInKB / 1024;
        return sizeInMB.toFixed(2) + " MB";
      }
    }
    return null;
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files[0];
    handleChange(droppedFiles);
  };

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <>
      <Modal className={"modal-wrapper max-w-[498px]"} isShow={isShow}>
        <form>
          <HeaderModal
            title={"test"}
            isShowHeader={true}
            className="modal-header pt-6 px-6 border-b border-[#EAECF0] pb-5"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg text-[#101828] font-interSemiBold leading-none pb-1.5">
                Change Vijilize Image
              </h2>
              <div className="flex items-center gap-x-1 -mt-2">
                <button
                  type="button"
                  onClick={startWalkthrough}
                  className="cursor-pointer hover:bg-[#F5F8FD] text-[#4F4D55] hover:text-[#1751D0] rounded p-1"
                  aria-label="Show guided walkthrough"
                  title="Show guided walkthrough"
                >
                  <HelpOutlineIcon sx={{ fontSize: 20 }} />
                </button>
                {/* Close Icon */}
                <img
                  className="modal-close modal-toggle cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1"
                  id="modal-toggle"
                  src={closeIcon}
                  onClick={onCloseModal}
                  alt="close icon"
                />
              </div>
            </div>

            <p className="text-sm text-[#475467]">
              Select from sample images or upload your own
            </p>
          </HeaderModal>

          <Body
            title=""
            isShowBody={true}
            className="modal-body px-6 pt-5 pb-5"
          >
            <div className="flex items-center gap-x-4 pb-2 border-b border-[#E4E7EC] mb-5">
              <button
                type="button"
                id="change-image-sample-tab"
                onClick={() => handleImages("sampleImg")}
                className={`font-interSemiBold py-2 px-3 rounded-lg hover:bg-[#E8E6FE] hover:text-[#1751D0] ${
                  manageImg === "sampleImg"
                    ? "text-[#1751D0] bg-[#E8E6FE]"
                    : "text-[#667085] bg-transparent"
                }`}
              >
                Sample Images
              </button>

              <button
                type="button"
                id="change-image-upload-tab"
                onClick={() => handleImages("customImg")}
                className={`font-interSemiBold py-2 px-3 rounded-lg hover:bg-[#E8E6FE] hover:text-[#1751D0] ${
                  manageImg === "customImg"
                    ? "text-[#1751D0] bg-[#E8E6FE]"
                    : "text-[#667085] bg-transparent"
                }`}
              >
                Upload Custom Image
              </button>
            </div>

            <div className="">
              {/* Sample Images */}
              {manageImg === "sampleImg" && (
                <div className="grid grid-cols-2 gap-4 overflow-y-auto max-h-[338px] pr-2">
                  {isLoadingImages ? (
                    <div className="col-span-2 w-full text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                      <p className="mt-4 text-sm text-[#475467]">
                        Loading sample images...
                      </p>
                    </div>
                  ) : sampleImages.length > 0 ? (
                    sampleImages.map((item: any, ind: number) => (
                      <div
                        onClick={() => handleImageSelection(ind)}
                        className={`border rounded-lg hover:border-[#1751D0] cursor-pointer p-1 transition-all ${
                          item.selected
                            ? "border-[#1751D0] ring-1 ring-[#1751D0]"
                            : "border-[#E4E7EC]"
                        }`}
                        key={ind}
                      >
                        <img
                          className="w-full h-[120px] rounded-lg object-cover"
                          src={item.url}
                          alt={item.fileName}
                        />
                      </div>
                    ))
                  ) : (
                    <div className="col-span-2 w-full text-center py-8">
                      <p className="text-sm text-[#475467]">
                        No sample images found
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Upload image */}
              {manageImg === "customImg" && (
                <>
                  <div className="" onDrop={handleDrop} onDragOver={onDragOver}>
                <input
                  type="file"
                  id="actual-btn"
                  onChange={(e: any) => handleChange(e.target.files[0])}
                  hidden
                  accept=".svg, .png, .jpg, .jpeg"
                />
                <label
                  className="file-upload block border border-[#EAECF0] hover:border-[#786CF9] rounded-xl py-4 px-6 text-center mb-6 cursor-pointer"
                  htmlFor="actual-btn"
                >
                  <img
                    className="mb-3 mx-auto"
                    src={allImgPaths.uploadFileIcon3}
                    alt=""
                  />
                  <div className="">
                    <span className="text-sm text-[#1751D0] font-interSemiBold">
                      Click to upload
                    </span>
                    <span className="text-sm text-[#475467]">
                      {" "}
                      or drag and drop
                    </span>
                    <p className="text-sm text-[#475467]">
                      SVG, PNG, JPG (max. 5 MB)
                    </p>
                  </div>
                </label>
              </div>

              {selectedFile && (
                <div className="block border-2 border-[#EAECF0] rounded-xl py-3.5 px-3.5 cursor-pointer mb-5">
                  <div className="flex gap-x-3 items-start">
                    <img src={allImgPaths.pdfIcon} alt="" />
                    <div className="w-full">
                      <div className="w-full">
                        <p className="text-sm text-[#475467] truncate max-w-[600px]">
                          {selectedFile?.name}
                        </p>
                      </div>
                      <p className="text-sm text-[#475467] mt-3">
                        {getFileSize(selectedFile?.size) || ""}
                      </p>
                      <div className="flex items-center gap-x-2" />
                    </div>
                  </div>
                </div>
              )}

              {imageSizeError && (
                <p className="mt-1 text-[#D92D20] text-sm font-interSemiBold">
                  Image should not be larger than 5 MB
                </p>
              )}
                </>
              )}
            </div>
          </Body>

          <FooterModal
            title=""
            isShowFooter={true}
            className="modal-footer pt-5 px-6 pb-4 border-t border-[#EAECF0]"
          >
            {isReadOnlyDemo && (
              <p className="text-center text-xs text-[#B54708] mb-3">
                {DEMO_LIMITED_MSG}
              </p>
            )}
            <div className="flex items-center gap-x-3 justify-end">
              <button
                onClick={onCloseModal}
                type="button"
                className={`text-[#344054] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-2.5 !text-base hover:bg-[#F2F4F7] w-40 ${
                  isUploading ? "button-disabled" : ""
                }`}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                id="change-image-submit"
                className={`!text-base text-white bg-[#1751D0] hover:bg-[#113B98] font-interSemiBold rounded-lg px-3.5 py-2.5 w-40 ${
                  isUploading || isReadOnlyDemo ? "button-disabled" : ""
                } `}
                type="button"
                disabled={isUploading || isReadOnlyDemo}
                title={isReadOnlyDemo ? DEMO_LIMITED_MSG : ""}
                onClick={changeImage}
              >
                {isUploading ? "Uploading..." : "Change"}
              </button>
            </div>
          </FooterModal>
        </form>
      </Modal>
    </>
  );
};

export default ChangeVijilizeImageModal;
